import {
  apiSlice,
  createClientV2MutationFn,
  createClientV2QueryFn,
} from 'store/slices/api';
import { parseQueryParams } from 'store/slices/utils';
import {
  ClientV2PATCHResponse,
  ClientV2POSTRequest,
  ClientV2POSTResponse,
  ClientV2ToggleRequest,
  ClientV2ToggleResponse,
  ListResponse,
  ClientV2PATCHRequest,
  QueryParams,
  ClientV2DELETERequest,
  ClientV2DELETEResponse,
} from 'types/api';
import {
  PurchaseRequestGroupSchema,
  PurchaseRequestGroupPOSTSchema,
  PurchaseRequestGroupPATCHSchema,
  PurchaseRequestSchema,
  PurchaseRequestPATCHSchema,
  SubmitPurchaseRequestGroupsForOrderRequest,
} from 'types/clientV2/purchaseRequests';
import { CLIENT_V2_URLS } from 'store/slices/constants/clientV2';

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPurchaseRequestsV2: builder.query<
      ListResponse<PurchaseRequestSchema>,
      QueryParams<PurchaseRequestSchema>
    >({
      queryFn: createClientV2QueryFn<
        ListResponse<PurchaseRequestSchema>,
        QueryParams<PurchaseRequestSchema>
      >((params) =>
        CLIENT_V2_URLS.PURCHASE_REQUESTS(
          parseQueryParams<PurchaseRequestSchema>(params)
        )
      ),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ id }: PurchaseRequestSchema) =>
                  ({ type: 'ClientV2PurchaseRequests', id } as const)
              ),
              { type: 'ClientV2PurchaseRequests', id: 'LIST' },
            ]
          : [{ type: 'ClientV2PurchaseRequests', id: 'LIST' }],
    }),
    updatePurchaseRequestsV2: builder.mutation<
      ClientV2PATCHResponse<PurchaseRequestSchema>,
      ClientV2PATCHRequest<PurchaseRequestPATCHSchema>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.PURCHASE_REQUESTS(),
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error) => [
        ...(result
          ? [
              ...result.updatedIds.map((id) => ({
                type: 'ClientV2PurchaseRequests' as const,
                id,
              })),
              {
                type: 'ClientV2PurchaseRequests' as const,
                id: 'LIST' as const,
              },
            ]
          : [
              {
                type: 'ClientV2PurchaseRequests' as const,
                id: 'LIST' as const,
              },
            ]),
        ...(result
          ? [
              ...result.updatedIds.map((id) => ({
                type: 'PurchaseRequests' as const,
                id,
              })),
              {
                type: 'PurchaseRequests' as const,
                id: 'LIST' as const,
              },
            ]
          : [
              {
                type: 'PurchaseRequests' as const,
                id: 'LIST' as const,
              },
            ]),
      ],
    }),
    deletePurchaseRequests: builder.mutation<
      ClientV2DELETEResponse<PurchaseRequestSchema>,
      ClientV2DELETERequest<PurchaseRequestSchema>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.PURCHASE_REQUESTS(),
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: (result, error, data) => [
        ...data.ids.map((id) => ({
          type: 'ClientV2PurchaseRequests' as const,
          id,
        })),
        { type: 'ClientV2PurchaseRequests', id: 'LIST' },
        { type: 'PurchaseRequests', id: 'LIST' },
        { type: 'PurchaseRequestGroups', id: 'LIST' },
      ],
    }),
    getPurchaseRequestGroups: builder.query<
      ListResponse<PurchaseRequestGroupSchema>,
      QueryParams<PurchaseRequestGroupSchema>
    >({
      queryFn: createClientV2QueryFn<
        ListResponse<PurchaseRequestGroupSchema>,
        QueryParams<PurchaseRequestGroupSchema>
      >((params) =>
        CLIENT_V2_URLS.PURCHASE_REQUEST_GROUPS(
          parseQueryParams<PurchaseRequestGroupSchema>(params)
        )
      ),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ id }: PurchaseRequestGroupSchema) =>
                  ({ type: 'PurchaseRequestGroups', id } as const)
              ),
              { type: 'PurchaseRequestGroups', id: 'LIST' },
            ]
          : [{ type: 'PurchaseRequestGroups', id: 'LIST' }],
    }),
    getPurchaseRequestGroup: builder.query<
      PurchaseRequestGroupSchema,
      {
        purchaseRequestGroupId: PurchaseRequestGroupSchema['id'];
        params?: QueryParams<PurchaseRequestGroupSchema>;
      }
    >({
      query: ({ purchaseRequestGroupId, params = {} }) =>
        CLIENT_V2_URLS.PURCHASE_REQUEST_GROUPS(
          `${purchaseRequestGroupId}${parseQueryParams<PurchaseRequestGroupSchema>(
            params
          )}`
        ),
      providesTags: (result) =>
        result
          ? [{ type: 'PurchaseRequestGroups', id: result.id }]
          : [{ type: 'PurchaseRequestGroups', id: 'LIST' }],
    }),
    createPurchaseRequestGroups: builder.mutation<
      ClientV2POSTResponse<PurchaseRequestGroupSchema>,
      ClientV2POSTRequest<PurchaseRequestGroupPOSTSchema>
    >({
      queryFn: createClientV2MutationFn<
        ClientV2POSTResponse<PurchaseRequestGroupSchema>,
        ClientV2POSTRequest<PurchaseRequestGroupPOSTSchema>
      >((payload) => ({
        url: CLIENT_V2_URLS.PURCHASE_REQUEST_GROUPS(),
        method: 'POST',
        body: payload,
      })),
      invalidatesTags: [{ type: 'PurchaseRequestGroups', id: 'LIST' }],
    }),
    updatePurchaseRequestGroups: builder.mutation<
      ClientV2PATCHResponse<PurchaseRequestGroupSchema>,
      ClientV2PATCHRequest<PurchaseRequestGroupPATCHSchema>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.PURCHASE_REQUEST_GROUPS(),
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [{ type: 'PurchaseRequestGroups', id: 'LIST' }],
    }),
    approvePurchaseRequestGroupsAction: builder.mutation<
      ClientV2ToggleResponse<PurchaseRequestGroupSchema>,
      ClientV2ToggleRequest<PurchaseRequestGroupSchema>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.PURCHASE_REQUEST_GROUPS_APPROVE(),
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, data) => [
        ...data.ids.map((id) => ({
          type: 'PurchaseRequestGroups' as const,
          id,
        })),
        { type: 'PurchaseRequests', id: 'LIST' },
      ],
    }),
    unapprovePurchaseRequestGroupsAction: builder.mutation<
      ClientV2ToggleResponse<PurchaseRequestGroupSchema>,
      ClientV2ToggleRequest<PurchaseRequestGroupSchema>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.PURCHASE_REQUEST_GROUPS_UNAPPROVE(),
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, data) => [
        ...data.ids.map((id) => ({
          type: 'PurchaseRequestGroups' as const,
          id,
        })),
        { type: 'PurchaseRequests', id: 'LIST' },
      ],
    }),
    voidPurchaseRequestGroupsAction: builder.mutation<
      ClientV2ToggleResponse<PurchaseRequestGroupSchema>,
      ClientV2ToggleRequest<PurchaseRequestGroupSchema>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.PURCHASE_REQUEST_GROUPS_VOID(),
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, data) => [
        ...data.ids.map((id) => ({
          type: 'PurchaseRequestGroups' as const,
          id,
        })),
        { type: 'PurchaseRequests', id: 'LIST' },
      ],
    }),
    unvoidPurchaseRequestGroupsAction: builder.mutation<
      ClientV2ToggleResponse<PurchaseRequestGroupSchema>,
      ClientV2ToggleRequest<PurchaseRequestGroupSchema>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.PURCHASE_REQUEST_GROUPS_UNVOID(),
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, data) => [
        ...data.ids.map((id) => ({
          type: 'PurchaseRequestGroups' as const,
          id,
        })),
        { type: 'PurchaseRequests', id: 'LIST' },
      ],
    }),
    archivePurchaseRequestGroupsAction: builder.mutation<
      ClientV2ToggleResponse<PurchaseRequestGroupSchema>,
      ClientV2ToggleRequest<PurchaseRequestGroupSchema>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.PURCHASE_REQUEST_GROUPS_ARCHIVE(),
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, data) => [
        ...data.ids.map((id) => ({
          type: 'PurchaseRequestGroups' as const,
          id,
        })),
        { type: 'PurchaseRequests', id: 'LIST' },
      ],
    }),
    unarchivePurchaseRequestGroupsAction: builder.mutation<
      ClientV2ToggleResponse<PurchaseRequestGroupSchema>,
      ClientV2ToggleRequest<PurchaseRequestGroupSchema>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.PURCHASE_REQUEST_GROUPS_UNARCHIVE(),
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, data) => [
        ...data.ids.map((id) => ({
          type: 'PurchaseRequestGroups' as const,
          id,
        })),
        { type: 'PurchaseRequests', id: 'LIST' },
      ],
    }),
    submitPurchaseRequestGroupsForOrder: builder.mutation<
      ClientV2ToggleResponse<PurchaseRequestGroupSchema>,
      {
        id: PurchaseRequestGroupSchema['id'];
        payload: SubmitPurchaseRequestGroupsForOrderRequest;
      }
    >({
      query: ({ id, payload }) => ({
        url: CLIENT_V2_URLS.PURCHASE_REQUEST_GROUPS_SUBMIT_FOR_ORDER(id),
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'PurchaseRequestGroups' as const, id },
        { type: 'PurchaseRequests', id: 'LIST' },
        { type: 'PurchaseRequestGroups', id: 'LIST' },
      ],
    }),
    approvePurchaseRequestGroupsForOrder: builder.mutation<
      ClientV2ToggleResponse<PurchaseRequestGroupSchema>,
      PurchaseRequestGroupSchema['id']
    >({
      query: (id) => ({
        url: CLIENT_V2_URLS.PURCHASE_REQUEST_GROUPS_APPROVE_FOR_ORDER(id),
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'PurchaseRequestGroups' as const, id },
        { type: 'PurchaseRequests', id: 'LIST' },
        { type: 'PurchaseRequestGroups', id: 'LIST' },
      ],
    }),
    deletePurchaseRequestGroups: builder.mutation<
      ClientV2DELETEResponse<PurchaseRequestGroupSchema>,
      ClientV2DELETERequest<PurchaseRequestGroupSchema>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.PURCHASE_REQUEST_GROUPS(),
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: (result, error, data) => [
        ...data.ids.map((id) => ({
          type: 'PurchaseRequestGroups' as const,
          id,
        })),
        { type: 'PurchaseRequestGroups', id: 'LIST' },
        { type: 'PurchaseRequests', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetPurchaseRequestsV2Query,
  useGetPurchaseRequestGroupsQuery,
  useGetPurchaseRequestGroupQuery,
  useCreatePurchaseRequestGroupsMutation,
  useUpdatePurchaseRequestGroupsMutation,
  useApprovePurchaseRequestGroupsActionMutation,
  useUnapprovePurchaseRequestGroupsActionMutation,
  useVoidPurchaseRequestGroupsActionMutation,
  useUnvoidPurchaseRequestGroupsActionMutation,
  useArchivePurchaseRequestGroupsActionMutation,
  useUnarchivePurchaseRequestGroupsActionMutation,
  useSubmitPurchaseRequestGroupsForOrderMutation,
  useApprovePurchaseRequestGroupsForOrderMutation,
  useDeletePurchaseRequestGroupsMutation,
  useUpdatePurchaseRequestsV2Mutation,
  useDeletePurchaseRequestsMutation,
  usePrefetch,
} = extendedApiSlice;
